import { useEffect, useState } from 'react';
import { Badge, Button, Page } from '@appwin/ui';
import { api, ApiError, type AdminApp, type Session, type Stats } from './api.js';
import { NewAppForm } from './NewAppForm.js';
import { AppDetail } from './AppDetail.js';

type Tab = 'apps' | 'moderation' | 'stats';

/**
 * Operator panel: the catalogue is filled by the winget import and by hand,
 * so everything here is about curating that list.
 */
export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .session()
      .then(setSession)
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.status === 401) return;
        setError(cause instanceof Error ? cause.message : 'Не удалось проверить сессию');
      })
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <Page>
        <p className="py-16 text-center text-sm text-muted">Загрузка…</p>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <div className="py-16 text-center">
          <p className="font-medium">Нет связи с API</p>
          <p className="mt-1 text-sm text-muted">{error}</p>
          <Button className="mt-4" onClick={() => window.location.reload()}>
            Повторить
          </Button>
        </div>
      </Page>
    );
  }

  if (!session) {
    return (
      <Page>
        <main className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center">
          <h1 className="text-2xl font-semibold">Админка Appwin</h1>
          <p className="mt-2 text-sm text-muted">
            Управление каталогом: импорт из winget, добавление и правка приложений.
          </p>
          <Button className="mt-6 w-full" onClick={() => api.signInWithGithub()}>
            Войти через GitHub
          </Button>
        </main>
      </Page>
    );
  }

  if (session.role !== 'admin') {
    return (
      <Page>
        <div className="py-16 text-center">
          <p className="font-medium">Нет доступа</p>
          <p className="mt-1 text-sm text-muted">
            Аккаунт {session.email} не входит в список администраторов.
          </p>
        </div>
      </Page>
    );
  }

  return <Panel session={session} />;
}

function Panel({ session }: { session: Session }) {
  const [tab, setTab] = useState<Tab>('apps');

  return (
    <Page>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border py-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted">Админка</p>
          <p className="font-medium">{session.displayName}</p>
        </div>
        <div className="flex items-center gap-2">
          <nav className="flex gap-1" role="tablist">
            {(
              [
                ['apps', 'Приложения'],
                ['moderation', 'Модерация'],
                ['stats', 'Статистика'],
              ] as Array<[Tab, string]>
            ).map(([value, label]) => (
              <button
                key={value}
                role="tab"
                aria-selected={tab === value}
                onClick={() => setTab(value)}
                className={`rounded-lg px-3 py-1.5 text-sm ${
                  tab === value ? 'bg-accent-soft text-accent' : 'text-muted hover:text-fg'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
          <Button variant="ghost" size="sm" onClick={() => void api.logout()}>
            Выйти
          </Button>
        </div>
      </header>

      <main className="py-6">
        {tab === 'apps' ? <AppsTab /> : null}
        {tab === 'moderation' ? <ModerationTab /> : null}
        {tab === 'stats' ? <StatsTab /> : null}
      </main>
    </Page>
  );
}

function AppsTab() {
  const [apps, setApps] = useState<AdminApp[] | null>(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [creating, setCreating] = useState(false);
  const [openApp, setOpenApp] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    api
      .apps(query, category || undefined)
      .then((page) => setApps(page.items))
      .catch(() => setApps([]))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
    // `load` is stable enough for this debounce; re-running on query/category.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, category]);

  if (openApp) {
    return <AppDetail appId={openApp} onBack={() => setOpenApp(null)} />;
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Поиск по названию или id"
          className="h-9 w-64 rounded-lg border border-border bg-surface px-3 text-sm"
        />
        <select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          className="h-9 rounded-lg border border-border bg-surface px-3 text-sm"
        >
          <option value="">Все категории</option>
          {CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <Button size="sm" onClick={() => setCreating(true)}>
          Добавить приложение
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={async () => {
            try {
              await api.runWingetImport();
              setNotice(
                'Импорт из winget запущен. Он идёт в фоне: обновляются версии и категории, это занимает пару минут.',
              );
            } catch (cause) {
              setNotice(cause instanceof ApiError ? cause.message : 'Не удалось запустить импорт');
            }
          }}
        >
          {busy ? 'Подождите…' : 'Импортировать из winget'}
        </Button>
      </div>

      {notice ? (
        <p className="mb-4 rounded-lg border border-border bg-surface p-3 text-sm text-muted">
          {notice}
        </p>
      ) : null}

      <p className="mb-2 text-xs text-muted">
        {apps ? `Найдено: ${apps.length}` : 'Загрузка…'}
      </p>

      {apps === null ? (
        <p className="py-8 text-center text-sm text-muted">Загрузка…</p>
      ) : apps.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted">
          Ничего не найдено. Измените фильтр или добавьте приложение.
        </p>
      ) : (
        <ul className="list-none space-y-1.5 p-0">
          {apps.map((app) => (
            <li
              key={app.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{app.name}</p>
                <p className="truncate text-xs text-muted">
                  {app.id} · {app.category}
                  {app.latestVersion ? ` · ${app.latestVersion}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge tone={app.origin === 'winget' ? 'neutral' : 'accent'}>
                  {app.origin === 'winget' ? 'winget' : 'вручную'}
                </Badge>
                <Badge tone={app.status === 'published' ? 'success' : 'warning'}>{app.status}</Badge>
                <span className="text-xs text-muted">{app.releaseCount} в.</span>
                <Button size="sm" variant="secondary" onClick={() => setOpenApp(app.id)}>
                  Открыть
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {creating ? (
        <NewAppForm
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      ) : null}

    </>
  );
}

function ModerationTab() {
  const [items, setItems] = useState<Awaited<ReturnType<typeof api.moderation>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    api
      .moderation()
      .then(setItems)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Ошибка'));
  };

  useEffect(load, []);

  if (error) return <p className="py-8 text-center text-sm text-danger">{error}</p>;
  if (!items) return <p className="py-8 text-center text-sm text-muted">Загрузка…</p>;

  if (items.items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-surface p-10 text-center">
        <p className="font-medium">Очередь пуста</p>
        <p className="mt-1 text-sm text-muted">
          Сюда попадают версии, добавленные не вами. Сейчас каталог наполняется импортом и вручную,
          поэтому проверять нечего.
        </p>
      </div>
    );
  }

  return (
    <ul className="list-none space-y-2 p-0">
      {items.items.map((item) => (
        <li key={item.release.id} className="rounded-xl border border-border bg-surface p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium">
                {item.appName} — {item.release.version}
              </p>
              <p className="truncate text-xs text-muted">{item.appId}</p>
              {item.release.downloadUrl ? (
                <a
                  href={item.release.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 block truncate text-xs text-accent hover:underline"
                >
                  {item.release.downloadUrl}
                </a>
              ) : null}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    await api.approve(item.release.id);
                    load();
                  } catch (cause) {
                    setError(cause instanceof ApiError ? cause.message : 'Ошибка');
                  }
                }}
              >
                Одобрить
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  const reason = window.prompt('Причина отклонения');
                  if (!reason) return;
                  try {
                    await api.reject(item.release.id, reason);
                    load();
                  } catch (cause) {
                    setError(cause instanceof ApiError ? cause.message : 'Ошибка');
                  }
                }}
              >
                Отклонить
              </Button>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted">
            ссылка: {item.checks.av === 'clean' ? 'проверена' : item.checks.av}
          </p>
        </li>
      ))}
    </ul>
  );
}

function StatsTab() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .stats()
      .then(setStats)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Ошибка'));
  }, []);

  if (error) return <p className="py-8 text-center text-sm text-danger">{error}</p>;
  if (!stats) return <p className="py-8 text-center text-sm text-muted">Загрузка…</p>;

  const total = stats.installs.total + stats.updates.total;
  const success = stats.installs.success + stats.updates.success;

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Установок" value={String(stats.installs.total)} />
        <Stat label="Обновлений" value={String(stats.updates.total)} />
        <Stat
          label="Успешных"
          value={total === 0 ? 'нет данных' : `${Math.round((success / total) * 100)}%`}
        />
        <Stat label="На модерации" value={String(stats.pendingModeration)} />
      </dl>

      {total === 0 ? (
        <p className="rounded-lg border border-border bg-surface p-4 text-sm text-muted">
          Отчётов об установке пока нет. Клиент присылает их, только если пользователь включил
          телеметрию в настройках, — это opt-in.
        </p>
      ) : null}

      {stats.topApps.length > 0 ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold">Чаще всего устанавливают</h2>
          <ul className="list-none space-y-1 p-0 text-sm">
            {stats.topApps.map((app) => (
              <li key={app.appId} className="flex justify-between rounded-lg bg-surface px-3 py-1.5">
                <span className="truncate">{app.name}</span>
                <span className="shrink-0 text-muted">
                  {app.installs} установок, {app.failures} ошибок
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {stats.topErrors.length > 0 ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold">Частые ошибки</h2>
          <ul className="list-none space-y-1 p-0 text-sm">
            {stats.topErrors.map((error) => (
              <li
                key={error.errorCode}
                className="flex justify-between rounded-lg bg-surface px-3 py-1.5"
              >
                <span className="font-mono text-xs">{error.errorCode}</span>
                <span className="text-muted">{error.count}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-1 text-xl font-semibold">{value}</dd>
    </div>
  );
}

const CATEGORIES = [
  'audio-video',
  'business',
  'communication',
  'development',
  'education',
  'games',
  'graphics',
  'health',
  'news',
  'productivity',
  'security',
  'system',
  'utilities',
];
